import { useState } from 'react';
import { Crown, Sparkles, Loader2, Calendar, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SubscriptionCardProps {
  plan: 'free' | 'premium';
  subscriptionEnd: string | null;
  onUpgrade?: () => void;
}

export function SubscriptionCard({ plan, subscriptionEnd, onUpgrade }: SubscriptionCardProps) {
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      toast.error('Erro ao abrir portal de gerenciamento');
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleUpgrade = async () => {
    if (onUpgrade) {
      onUpgrade();
      return;
    }
    
    setLoadingCheckout(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout');
      
      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erro ao criar sessão de pagamento');
    } finally {
      setLoadingCheckout(false);
    }
  };

  if (plan === 'premium') {
    return (
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-800">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Crown className="h-5 w-5 text-amber-500" />
            Plano Premium
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Você tem acesso ilimitado a todas as funcionalidades!
            </p>
            {subscriptionEnd && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>
                  Próxima renovação: {format(new Date(subscriptionEnd), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </span>
              </div>
            )}
          </div>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleManageSubscription}
            disabled={loadingPortal}
          >
            {loadingPortal ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Carregando...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Gerenciar assinatura
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-5 w-5 text-muted-foreground" />
          Plano Gratuito
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Limite de 3 identificações/diagnósticos por mês e 3 plantas cadastradas.
          </p>
          <div className="rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-3">
            <p className="text-sm font-medium">
              Upgrade para Premium: <span className="text-primary">R$ 15/mês</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Uso ilimitado de todas as funcionalidades
            </p>
          </div>
        </div>
        <Button 
          className="w-full"
          onClick={handleUpgrade}
          disabled={loadingCheckout}
        >
          {loadingCheckout ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Carregando...
            </>
          ) : (
            <>
              <Crown className="mr-2 h-4 w-4" />
              Assinar Premium
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
